import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, ChevronDown, ChevronRight, Loader2, XCircle } from 'lucide-react';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { unwrapArray } from '../../lib/api-envelope';
import { fmtSignedCurrency } from '../../lib/display-labels';
import { formatInstant } from '../../lib/calendar-date';
import { isRefusal, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import {
  billingFlagsKey,
  billingFlagsStage,
  canAnswerBillingFlags,
  useTicketBillingFlags,
} from '../../hooks/useTicketBillingFlags';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import type { BillingFlag, BillingFlagDirection } from '../../types/billing-flags';

/**
 * PRE-FLIGHT CHECKS ON A FIELD-APPROVED TICKET (brief FE-1, api#908).
 *
 * v1 is SUGGEST: the panel offers, it never edits the ticket and it never
 * blocks approval. The agent flags, the office decides — Resolve means "I've
 * dealt with this", Dismiss means "this is wrong for this ticket" and carries
 * a reason that feeds the learning loop.
 *
 * SHADOW IS INVISIBLE. Outside `suggest` this renders nothing at all: no empty
 * state and no "the agent is watching" line, even if the payload carried rows.
 *
 * Every figure here is the API's. The message is rendered verbatim and the
 * estimate is only formatted — never rebuilt from the lines on the page.
 */

export const BILLING_FLAGS_PANEL_ID = 'billing-flags-panel';

/** `+$150.00 est.` / `−$500.00 est.` / `$0.00 est.`; an absent estimate is `—`, never `$0.00`. */
export function fmtFlagEstimate(delta: number | null | undefined): string {
  const money = fmtSignedCurrency(delta);
  return money === '—' ? '—' : `${money} est.`;
}

/**
 * Direction → badge and rail. Exhaustive over the API's CHECK constraint; an
 * unknown value gets a slate dash and a transparent rail, never a guessed
 * direction. "Over" takes blue, not red: an over-bill is a question for the
 * office, not a fault, and red already means rejected on this page.
 */
const DIRECTION_SKIN: Record<BillingFlagDirection, { label: string; fill: string; rail: string }> = {
  under: { label: 'Under', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  over: { label: 'Over', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  none: { label: 'Check', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
const UNKNOWN_DIRECTION = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
const directionSkin = (d: string) => DIRECTION_SKIN[d as BillingFlagDirection] ?? UNKNOWN_DIRECTION;

function DirectionBadge({ direction }: { direction: string }) {
  const skin = directionSkin(direction);
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

const SECONDARY =
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-xs font-bold text-white transition hover:bg-fieldiq-dark disabled:cursor-not-allowed disabled:opacity-50';

const statusOf = (err: unknown) => (err as { response?: { status?: number } })?.response?.status;
const apiErrorOf = (err: unknown) =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error;

const CONFLICT_LINE = 'This check was already answered or has changed — reloaded.';
const REASON_MIN = 3;
const REASON_MAX = 500;

export function BillingFlagsPanel({ ticketId, ticketStatus }: { ticketId: string; ticketStatus: string }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();
  const stage = billingFlagsStage(ticketStatus);
  const canAnswer = canAnswerBillingFlags(user?.role) && stage === 'open';

  const query = useTicketBillingFlags(ticketId, ticketStatus, user?.role);
  const failure = queryLoadFailure(query);
  const data = query.data;

  const [answeredOpen, setAnsweredOpen] = useState(false);
  const [rowNotice, setRowNotice] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<BillingFlag | null>(null);
  const [reason, setReason] = useState('');
  const [dialogError, setDialogError] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: billingFlagsKey(ticketId) });

  const flags = data?.mode === 'suggest' ? data.flags : [];
  const open = flags.filter(f => f.status === 'open');
  const answered = flags.filter(f => f.status !== 'open');

  // WHO ANSWERED: the flag carries a user id, not a name. Look the names up
  // only when an answered flag names someone other than the reader. A name
  // the roster does not have renders the dash, never the raw id.
  const needsNames = answered.some(f => f.resolvedBy && f.resolvedBy !== user?.id);
  const { data: names } = useQuery({
    queryKey: ['users', 'billing-flag-names'],
    queryFn: async () => {
      const rows = unwrapArray<{ id: string; fullName?: string | null }>((await api.users.list()).data);
      return new Map(rows.map(r => [r.id, r.fullName ?? null]));
    },
    enabled: needsNames,
  });
  const whoName = (id: string | null) => {
    if (!id) return '—';
    if (id === user?.id) return 'you';
    return names?.get(id) || '—';
  };

  const resolveMutation = useMutation({
    mutationFn: (flagId: string) => api.billingFlags.resolve(ticketId, flagId),
    onSuccess: () => {
      setRowNotice(null);
      refresh();
    },
    onError: (err) => {
      setRowNotice(statusOf(err) === 409 ? CONFLICT_LINE : apiErrorOf(err) ?? 'Could not resolve this check.');
      if (statusOf(err) === 409) refresh();
    },
  });

  const dismissMutation = useMutation({
    mutationFn: ({ flagId, text }: { flagId: string; text: string }) =>
      api.billingFlags.dismiss(ticketId, flagId, text),
    onSuccess: () => {
      closeDismiss();
      refresh();
    },
    onError: (err) => {
      if (statusOf(err) === 409) {
        setDialogError(CONFLICT_LINE);
        refresh();
      } else {
        setDialogError(apiErrorOf(err) ?? 'Could not dismiss this check.');
      }
    },
  });

  const openDismiss = (flag: BillingFlag) => {
    setDismissing(flag);
    setReason('');
    setDialogError(null);
  };
  const closeDismiss = () => {
    setDismissing(null);
    setReason('');
    setDialogError(null);
  };

  if (!stage) return null;

  // A REFUSAL RENDERS NOTHING: this panel belongs to a role the server says
  // the reader is not, so a "you don't have access" line would announce a
  // feature that is not theirs. Any other failure is said, in the panel's
  // place, and is never the empty line below.
  if (failure) {
    if (isRefusal(queryFailureError(query))) return null;
    return (
      <PanelShell>
        <LoadFailureNotice error={queryFailureError(query)} subject="pre-flight checks" className="px-4 py-6" />
      </PanelShell>
    );
  }

  // Nothing until the mode is known: a shell drawn while loading would show a
  // shadow tenant that a panel exists.
  if (!data || data.mode !== 'suggest') return null;

  if (stage === 'answered') {
    if (answered.length === 0) return null;
    return (
      <PanelShell>
        <button
          type="button"
          onClick={() => setAnsweredOpen(v => !v)}
          aria-expanded={answeredOpen}
          aria-controls={`${BILLING_FLAGS_PANEL_ID}-answered`}
          className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-bold text-white hover:bg-fieldiq-dark/50"
        >
          {answeredOpen ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
          Pre-flight checks ({answered.length} answered)
        </button>
        <div id={`${BILLING_FLAGS_PANEL_ID}-answered`}>
          {answeredOpen && <AnsweredList flags={answered} whoName={whoName} timeZone={timeZone} />}
        </div>
      </PanelShell>
    );
  }

  const reasonLength = reason.trim().length;
  const reasonValid = reasonLength >= REASON_MIN && reasonLength <= REASON_MAX;

  return (
    <PanelShell
      header
      footer={canAnswer ? null : 'Only office staff and admins can resolve these.'}
    >
      {rowNotice && (
        <p role="status" className="border-b border-fieldiq-border px-4 py-2 text-sm text-fieldiq-text">
          {rowNotice}
        </p>
      )}

      {flags.length === 0 ? (
        <p className="px-4 py-6 text-sm text-fieldiq-muted">No pre-flight checks raised for this version.</p>
      ) : (
        <>
          {open.length > 0 && (
            <>
              {/* md and up: the register table. */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[720px] text-sm">
                  <thead>
                    <tr className="border-b border-l-4 border-fieldiq-border border-l-transparent text-left text-[0.65rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <th className="px-4 py-2">Check</th>
                      <th className="px-4 py-2 text-right">Estimate</th>
                      <th className="px-4 py-2">Direction</th>
                      {canAnswer && <th className="px-4 py-2 text-right"><span className="sr-only">Actions</span></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {open.map(flag => (
                      <tr
                        key={flag.id}
                        data-testid="billing-flag-open"
                        className={`border-b border-l-4 border-fieldiq-border last:border-b-0 ${directionSkin(flag.direction).rail}`}
                      >
                        <td className="px-4 py-3 text-white">{flag.message}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right font-bold text-white">
                          {fmtFlagEstimate(flag.estimatedDelta)}
                        </td>
                        <td className="px-4 py-3"><DirectionBadge direction={flag.direction} /></td>
                        {canAnswer && (
                          <td className="px-4 py-3">
                            <FlagActions
                              flag={flag}
                              busy={resolveMutation.isPending && resolveMutation.variables === flag.id}
                              onResolve={() => resolveMutation.mutate(flag.id)}
                              onDismiss={() => openDismiss(flag)}
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Below md: the same rows as stacked cards, no rail. */}
              <ul className="divide-y divide-fieldiq-border md:hidden">
                {open.map(flag => (
                  <li key={flag.id} className="space-y-2 px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <DirectionBadge direction={flag.direction} />
                      <span className="font-bold text-white">{fmtFlagEstimate(flag.estimatedDelta)}</span>
                    </div>
                    <p className="text-sm text-white">{flag.message}</p>
                    {canAnswer && (
                      <FlagActions
                        flag={flag}
                        busy={resolveMutation.isPending && resolveMutation.variables === flag.id}
                        onResolve={() => resolveMutation.mutate(flag.id)}
                        onDismiss={() => openDismiss(flag)}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          {answered.length > 0 && (
            <div className={open.length > 0 ? 'border-t border-fieldiq-border' : ''}>
              <p className="px-4 pt-3 text-[0.65rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                Answered ({answered.length})
              </p>
              <AnsweredList flags={answered} whoName={whoName} timeZone={timeZone} />
            </div>
          )}
        </>
      )}

      <Dialog open={!!dismissing} onOpenChange={o => { if (!o) closeDismiss(); }} className="z-[60]">
        <DialogContent>
          <div role="dialog" aria-modal="true" aria-labelledby="dismiss-flag-title">
            <DialogHeader>
              <DialogTitle>
                <span id="dismiss-flag-title">Dismiss this check</span>
              </DialogTitle>
            </DialogHeader>
            {dismissing && <p className="mb-3 text-sm text-fieldiq-text">{dismissing.message}</p>}
            <label htmlFor="dismiss-flag-reason" className="mb-1 block text-xs font-bold text-white">
              Why is this wrong for this ticket?
            </label>
            <textarea
              id="dismiss-flag-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              maxLength={REASON_MAX}
              rows={4}
              autoFocus
              className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
            <p className="mt-1 text-right text-xs text-fieldiq-muted" aria-live="polite">
              {reasonLength} / {REASON_MAX}
            </p>
            {dialogError && (
              <p role="alert" className="mt-2 text-sm text-fieldiq-text">{dialogError}</p>
            )}
            <DialogFooter>
              <div className="mt-4 flex justify-end gap-2">
                <button type="button" className={SECONDARY} onClick={closeDismiss}>Cancel</button>
                <button
                  type="button"
                  className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!reasonValid || dismissMutation.isPending || dialogError === CONFLICT_LINE}
                  onClick={() => dismissing && dismissMutation.mutate({ flagId: dismissing.id, text: reason.trim() })}
                >
                  {dismissMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                  Dismiss check
                </button>
              </div>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </PanelShell>
  );
}

function PanelShell({
  header,
  footer,
  children,
}: {
  header?: boolean;
  footer?: string | null;
  children: React.ReactNode;
}) {
  return (
    <section
      id={BILLING_FLAGS_PANEL_ID}
      data-testid="billing-flags-panel"
      aria-labelledby={header ? `${BILLING_FLAGS_PANEL_ID}-title` : undefined}
      aria-label={header ? undefined : 'Pre-flight checks'}
      className="scroll-mt-4 overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
    >
      {header && (
        <div className="border-b border-fieldiq-border px-4 py-3">
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Billing agent</p>
          <h2 id={`${BILLING_FLAGS_PANEL_ID}-title`} className="mt-1 text-base font-extrabold tracking-tight text-white">
            Pre-flight checks
          </h2>
        </div>
      )}
      {children}
      {footer && <p className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">{footer}</p>}
    </section>
  );
}

function FlagActions({
  flag,
  busy,
  onResolve,
  onDismiss,
}: {
  flag: BillingFlag;
  busy: boolean;
  onResolve: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="flex justify-end gap-2">
      <button type="button" className={SECONDARY} onClick={onResolve} disabled={busy} aria-label={`Resolve: ${flag.message}`}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} aria-hidden="true" />}
        Resolve
      </button>
      <button type="button" className={SECONDARY} onClick={onDismiss} aria-label={`Dismiss: ${flag.message}`}>
        <XCircle size={13} aria-hidden="true" />
        Dismiss
      </button>
    </div>
  );
}

function AnsweredList({
  flags,
  whoName,
  timeZone,
}: {
  flags: BillingFlag[];
  whoName: (id: string | null) => string;
  timeZone: string | null;
}) {
  return (
    <ul className="divide-y divide-fieldiq-border">
      {flags.map(flag => (
        // Set back by INK, not by opacity: an opacity group fades the badge and the
        // muted meta line along with everything else, and in light theme that
        // measured 3.12 on the meta line and 3.47 on the badge.
        <li key={flag.id} data-testid="billing-flag-answered" className="space-y-1 px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <DirectionBadge direction={flag.direction} />
            <span className="text-sm font-bold text-fieldiq-text">{fmtFlagEstimate(flag.estimatedDelta)}</span>
          </div>
          <p className="text-sm text-fieldiq-text">{flag.message}</p>
          <p className="text-xs text-fieldiq-muted">
            {flag.status === 'dismissed' ? 'Dismissed' : flag.status === 'resolved' ? 'Resolved' : '—'} by{' '}
            {whoName(flag.resolvedBy)} · {formatInstant(flag.resolvedAt, timeZone) ?? '—'}
          </p>
          {flag.status === 'dismissed' && (
            <p className="text-xs text-fieldiq-text">Reason: {flag.dismissReason ?? '—'}</p>
          )}
        </li>
      ))}
    </ul>
  );
}
